import { useState, useEffect } from "react";
import { team } from "../data/teamData";
import { palette } from "../style_palete";

export default function Team() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovering, setIsHovering] = useState(false);

  const next = () => setCurrentIndex((prev) => (prev + 1) % team.length);
  const prev = () => setCurrentIndex((prev) => (prev - 1 + team.length) % team.length);

  useEffect(() => {
    if (isHovering) return;
    
    const id = setInterval(next, 6000);
    return () => clearInterval(id);
  }, [isHovering]);

  const current = team[currentIndex];

  // Función para obtener iniciales del nombre
  const getInitials = (name) => {
    return name
      .split(" ")
      .map(word => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <section 
      id="equipo" 
      className="py-28 md:py-32"
      style={{ backgroundColor: palette.sky }}
    >
      <div className="relative mx-auto max-w-7xl px-4 md:px-6">
        {/* Título - Versión anterior */}
        <div className="max-w-2xl mb-16">
          <div className="mb-6">
            <h2 className="text-3xl md:text-4xl font-semibold text-[#0F1B2D] mb-3">
              Nuestro Equipo
            </h2>
            <div className="w-36 h-1.5 bg-[#2E8BC0] rounded-full"></div>
          </div>

          <p className="text-[#5E738A] text-lg mt-5">
            Profesionales dedicados a acompañarte en cada etapa de tu recuperación.
          </p>
        </div>

        {/* Contenedor de la card con navegación */}
        <div 
          className="relative max-w-5xl mx-auto"
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >

          {/* Card principal con altura mínima fija */}
          <div className="min-h-[0px]"> {/* Altura mínima fija */}
            <div
              className="rounded-xl bg-gradient-to-br from-white to-[#F7FAFD] border border-[#E1EBF5] p-7 text-[#0F1B2D] shadow-sm
                transition-all duration-400 hover:shadow-md hover:border-[#2E8BC0]/20 h-full"
            >
              <div className="flex flex-col md:flex-row items-start gap-7 h-full">
                {/* IMAGEN CUADRADA (derecha en desktop) */}
                <div className="hidden md:block md:order-1 w-full md:w-2/5"> {/* hidden md:block = oculta en móvil, muestra en desktop */}
                  <div className="relative">
                    {/* Contenedor de imagen CUADRADA con bordes redondeados */}
                    <div className="w-full aspect-square max-w-[280px] mx-auto md:mx-0 rounded-2xl overflow-hidden 
                      border-4 border-white shadow-lg bg-gradient-to-br from-[#E1EBF5] to-[#2E8BC0]/10">
                      {current.image ? (
                        <img
                          src={current.image}
                          alt={current.name}
                          className="h-full w-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        // Avatar con iniciales en formato cuadrado
                        <div className="h-full w-full flex items-center justify-center 
                          bg-gradient-to-br from-[#2E8BC0] to-[#0F4C81]">
                          <span className="text-white text-5xl md:text-6xl font-bold">
                            {getInitials(current.name)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>


                {/* INFO (izquierda en desktop) */}
                <div className="order-1 md:order-2 flex-1 w-full md:w-3/5 text-center md:text-left">
                  <div className="space-y-5 h-full flex flex-col">
                    {/* Nombre y rol */}
                    <div className="space-y-1.5">
                      <h3 className="text-xl md:text-2xl font-semibold tracking-tight text-[#0F1B2D]">
                        {current.name}
                      </h3>
                      <p className="text-[#2E8BC0] font-medium text-sm uppercase tracking-wide">
                        {current.role}
                      </p>
                    </div>

                    {/* Quote */}
                    {current.quote && (
                      <div className="space-y-3">
                        <p className="text-[#627180] italic text-base md:text-lg leading-relaxed border-l-4 border-[#2E8BC0]/30 pl-4 py-1">
                          "{current.quote}"
                        </p>
                      </div>
                    )}

                    {/* Bio */}
                    {current.bio && (
                      <p className="text-[#5E738A] text-sm md:text-base leading-relaxed">
                        {current.bio}
                      </p>
                    )}

                    {/* Especialidades - Siempre en la parte inferior */}
                    {current.specialties && current.specialties.length > 0 && (
                      <div className="pt-3 mt-auto"> {/* mt-auto empuja esto hacia abajo */}
                        <h4 className="text-xs font-medium text-[#627180] mb-2.5 uppercase tracking-wide">
                          ESPECIALIDADES
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {current.specialties.map((specialty, index) => (
                            <span
                              key={index}
                              className="px-3 py-1.5 bg-[#E1EBF5] text-[#2E8BC0] 
                                rounded-lg text-xs font-medium border border-[#2E8BC0]/20"
                            >
                              {specialty}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Indicadores y contador */}
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 mt-9 pt-7 
            border-t border-[#E1EBF5]">
            {/* Indicadores */}
            <div className="flex gap-2">
              {team.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`transition-all duration-300 ${
                    index === currentIndex
                      ? "opacity-100"
                      : "opacity-50 hover:opacity-80"
                  }`}
                  aria-label={`Ir al miembro ${index + 1}`}
                >
                  <div
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      index === currentIndex
                        ? "w-7 bg-[#2E8BC0]"
                        : "w-1.5 bg-[#D6E4EF]"
                    }`}
                  />
                </button>
              ))}
            </div>
            
            {/* Contador y navegación */}
            <div className="flex items-center gap-5">
              {/* Contador */}
              <div className="text-sm text-[#5E738A]">
                <span className="text-[#2E8BC0] font-medium">{currentIndex + 1}</span>
                <span className="mx-1 text-[#A7C3D9]">/</span>
                <span className="text-[#627180]">{team.length}</span>
              </div>
              
              {/* Botones de navegación */}
              <div className="flex gap-2">
                <button
                  onClick={prev}
                  className="p-2 rounded-full bg-white border border-[#E1EBF5] 
                    hover:border-[#2E8BC0]/30 hover:shadow-sm transition-all"
                  aria-label="Anterior"
                >
                  <svg className="w-3.5 h-3.5 text-[#2E8BC0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={next}
                  className="p-2 rounded-full bg-white border border-[#E1EBF5] 
                    hover:border-[#2E8BC0]/30 hover:shadow-sm transition-all"
                  aria-label="Siguiente"
                >
                  <svg className="w-3.5 h-3.5 text-[#2E8BC0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Nota sobre el equipo completo */}
        <p className="text-center text-[#5E738A] text-xs md:text-sm mt-7">
          Conoce a nuestros profesionales especializados
        </p>
      </div>
    </section>
  );
}